import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type AnalyticsPeriod, type BranchComparison } from "../../api";
import { fmtMoney } from "../../lib/format";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { EmptyState } from "../ui/empty-state";
import { Skeleton } from "../ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

export function BranchComparisonCard({
  period,
  enabled,
}: {
  period: AnalyticsPeriod;
  enabled: boolean;
}) {
  const { t } = useTranslation("analytics");
  const [data, setData] = useState<BranchComparison | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    api
      .analyticsComparison(period)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period, enabled]);

  if (!enabled) {
    // pro/standard — upsell: dimmed + "Max" badge. Sahifaning OXIRIDA turadi:
    // ilgari u eng birinchi blok edi, ya'ni ega o'z raqamlarini ko'rmasidan
    // oldin reklama o'qirdi.
    return (
      <Card className="opacity-60">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">{t("comparison.title")}</CardTitle>
          <Badge variant="outline">{t("comparison.maxBadge")}</Badge>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t("comparison.upsell")}</p>
        </CardContent>
      </Card>
    );
  }

  // Max'da bo'lsa-yu, hali ko'p filial bo'lmasa — taqqoslash ko'rsatmaymiz.
  if (!loading && data && data.rows.length <= 1) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t("comparison.title")}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading && !data ? (
          <div className="p-4">
            <Skeleton className="h-32" />
          </div>
        ) : !data || data.rows.length === 0 ? (
          <EmptyState title={t("empty.noData")} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("comparison.columns.branch")}</TableHead>
                <TableHead className="text-right">{t("comparison.columns.revenue")}</TableHead>
                <TableHead className="text-right">{t("comparison.columns.works")}</TableHead>
                <TableHead className="text-right">{t("comparison.columns.avgCheck")}</TableHead>
                <TableHead>{t("comparison.columns.topMaster")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.branch_id ?? "none"} className={r.is_active ? "" : "opacity-60"}>
                  <TableCell className="font-medium">
                    {r.branch_name}
                    {!r.is_active && (
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        {t("comparison.archived")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmtMoney(r.daromad)}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.ish_soni}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtMoney(r.ortacha_chek)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {r.top_usta_name ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
